import { useState, useEffect, useRef } from "react";
import { notifications } from "../../services/api";
import "./navbar.css";
import logo from "../../assets/images/logo.png";

import { NavLink, Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Notification State
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [notifList, setNotifList] = useState([]);
  const notifRef = useRef(null);

  // Poll for unread count
  useEffect(() => {
    if (!user) return;

    // Initial fetch
    fetchUnread();

    const interval = setInterval(fetchUnread, 30000); // 30s
    return () => clearInterval(interval);
  }, [user]);

  // Click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifRef]);

  const fetchUnread = async () => {
    try {
      const res = await notifications.getUnreadCount();
      setUnreadCount(res.data.count);
    } catch (err) {
      console.error("Failed to fetch notification count", err);
    }
  };

  const handleToggleNotif = async () => {
    const newState = !showNotifDropdown;
    setShowNotifDropdown(newState);
    if (newState) {
      try {
        const res = await notifications.getAll();
        setNotifList(res.data);
        // Optionally mark read on click, or let user manually mark?
        // "Mark all as read" button is better.
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notifications.markAllRead();
      setUnreadCount(0);
      setNotifList(list => list.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error(err);
    }
  };

  const handleNotifClick = async (n) => {
    if (!n.is_read) {
      try {
        await notifications.markRead(n.id);
        setUnreadCount(c => Math.max(0, c - 1));
        setNotifList(list => list.map(item => item.id === n.id ? { ...item, is_read: true } : item));
      } catch (err) { console.error(err); }
    }
    // Navigate based on type?
    setShowNotifDropdown(false);
    if (n.type === 'CONSULTATION_UPDATE') {
      navigate(user.role === 'admin' ? '/admin/consultations' : '/appointments');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const changeLang = (lng) => i18n.changeLanguage(lng);

  return (
    <div className="navbar-container">
      <div className="navbar-inner">
        <div className="logo">
          <Link to="/"><img src={logo} alt="medic-logo" /></Link>
        </div>

        <ul className="nav-items">
          <li><NavLink to="/doctor" className={({ isActive }) => isActive ? "active" : ""}>{t('nav.doctor')}</NavLink></li>
          {user && user.role === 'admin' && (
            <li><NavLink to="/admin" className={({ isActive }) => isActive ? "active" : ""}>Admin</NavLink></li>
          )}
          {user && user.role === 'patient' && (
            <>
              <li><NavLink to="/dashboard" className={({ isActive }) => isActive ? "active" : ""}>{t('nav.dashboard')}</NavLink></li>
              <li><NavLink to="/assessment" className={({ isActive }) => isActive ? "active" : ""}>Assessment</NavLink></li>
              <li><NavLink to="/appointments" className={({ isActive }) => isActive ? "active" : ""}>Appointments</NavLink></li>
            </>
          )}
          {user && user.role === 'doctor' && (
            <>
              <li><NavLink to="/doctor/calendar" className={({ isActive }) => isActive ? "active" : ""}>Schedule</NavLink></li>
              <li><NavLink to="/doctor/create-meeting" className={({ isActive }) => isActive ? "active" : ""}>New Meeting</NavLink></li>
            </>
          )}
        </ul>

        <div className="side-nav-items">
          {user && (
            <div className="nav-bell-container" onClick={handleToggleNotif} ref={notifRef}>
              <div className="nav-bell-icon">🔔</div>
              {unreadCount > 0 && <div className="nav-bell-badge">{unreadCount}</div>}

              {showNotifDropdown && (
                <div className="notif-dropdown" onClick={e => e.stopPropagation()}>
                  <div className="notif-header">
                    <span>Notifications</span>
                    <button onClick={handleMarkAllRead}>Mark all read</button>
                  </div>
                  <div className="notif-list">
                    {notifList.length === 0 ? (
                      <div className="notif-empty">No notifications</div>
                    ) : (
                      notifList.map(n => (
                        <div key={n.id} className={`notif-item ${n.is_read ? '' : 'unread'}`} onClick={() => handleNotifClick(n)}>
                          <div className="notif-item-title">{n.title}</div>
                          <div className="notif-item-message">{n.message}</div>
                          <div className="notif-item-time">{new Date(n.created_at).toLocaleString()}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {user ? (
            <div className="navbar-profile-container" style={{ position: 'relative' }}>
              <div
                className="navbar-profile"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}
              >
                <span>{user.email?.split('@')[0]}</span>
                <span style={{ fontSize: '0.8em' }}>▼</span>
              </div>

              {dropdownOpen && (
                <div className="navbar-dropdown">
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      navigate(user.role === 'patient' ? '/dashboard' : '/doctor');
                      setDropdownOpen(false);
                    }}
                  >
                    Profil
                  </div>
                  <div
                    className="dropdown-item logout-item"
                    onClick={() => {
                      handleLogout();
                      setDropdownOpen(false);
                    }}
                  >
                    Déconnexion
                  </div>
                </div>
              )}
            </div>
          ) : (
            <NavLink to="/login" className="cta">{t('site.login')}</NavLink>
          )}

          <div className="lang-switch" role="toolbar" aria-label="Language selector">
            <button className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`} onClick={() => changeLang('en')} title="English">
              {/* simple UK/US flag svg */}
              <svg width="20" height="14" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <rect width="60" height="30" fill="#012169" />
                <g fill="#fff">
                  <rect x="0" y="13" width="60" height="4" />
                  <rect x="28" y="0" width="4" height="30" />
                </g>
                <g fill="#C8102E">
                  <rect x="0" y="14" width="60" height="2" />
                  <rect x="29" y="0" width="2" height="30" />
                </g>
              </svg>
            </button>
            <button className={`lang-btn ${i18n.language === 'fr' ? 'active' : ''}`} onClick={() => changeLang('fr')} title="Français">
              {/* French flag svg */}
              <svg width="20" height="14" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <rect width="20" height="30" fill="#0055A4" />
                <rect x="20" width="20" height="30" fill="#FFF" />
                <rect x="40" width="20" height="30" fill="#EF4135" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
